import type { ControllerRenderProps } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MoveUp, RefreshCcw } from "lucide-react";

import { cn } from "@/lib/utils";
import { Textarea } from "@/components/primitives/textarea";
import { Button } from "@/components/primitives/button";
import { IS_MOBILE } from "@/lib/environmentDetector";
import type { AIChatFormType } from "@/schemas/chat";
import type { MessageLimitState } from "@/lib/messageLimits";
import type { RecordingStatus } from "@/lib/voice";

import MobileAttachmentMenu from "../rtfInput/attachment/menu/MobileAttachmentMenu";
import DesktopAttachmentMenu from "../rtfInput/attachment/menu/DesktopAttachmentMenu";
import FileAttachmentPreview from "../FileAttachmentPreview";
import MediaAttachmentPreview from "../MediaAttachmentPreview";
import RecordVoiceMessageButton from "../rtfInput/RecordVoiceMessageButton";
import {
  VoiceRecordingBar,
  LockIndicator,
} from "../rtfInput/VoiceRecordingBar";

type Props = {
  field: ControllerRenderProps<AIChatFormType, "prompt">;
  textareaRef: React.MutableRefObject<HTMLTextAreaElement | null>;
  isMultiLine: boolean;
  hasAttachment: boolean;
  handleKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  handleTextareaResize: (e: React.FormEvent<HTMLTextAreaElement>) => void;
  isRecording: boolean;
  isLocked: boolean;
  duration: number;
  isNearMaxDuration: boolean;
  liveAmplitude: number;
  slideProgress: number;
  lockProgress: number;
  onLockedSend: () => void | Promise<void>;
  onLockedCancel: () => void;
  canSendMessage: boolean;
  canSubmit: boolean;
  isSubmitting: boolean;
  voiceRecordingStatus: RecordingStatus;
  requestMic: () => Promise<void>;
  onStartRecording: () => Promise<void>;
  onGestureStart: (origin: { x: number; y: number }) => void;
  onImmediateSend: () => void;
  onImmediateLock: () => void;
  showCounter: boolean;
  isOverLimit: boolean;
  messageLimits: MessageLimitState;
};

export default function NewAIChatInput({
  field,
  textareaRef,
  isMultiLine,
  hasAttachment,
  handleKeyDown,
  handleTextareaResize,
  isRecording,
  isLocked,
  duration,
  isNearMaxDuration,
  liveAmplitude,
  slideProgress,
  lockProgress,
  onLockedSend,
  onLockedCancel,
  canSendMessage,
  canSubmit,
  isSubmitting,
  voiceRecordingStatus,
  requestMic,
  onStartRecording,
  onGestureStart,
  onImmediateSend,
  onImmediateLock,
  showCounter,
  isOverLimit,
  messageLimits,
}: Props) {
  const { t } = useTranslation();

  return (
    <div
      className={cn(
        "w-full border border-border bg-transparent",
        !isMultiLine && !hasAttachment && !isRecording
          ? "rounded-full"
          : "rounded-xl",
      )}
    >
      {isRecording ? (
        <div className="flex flex-col gap-2 px-4 py-3">
          <VoiceRecordingBar
            duration={duration}
            isNearMaxDuration={isNearMaxDuration}
            liveAmplitude={liveAmplitude}
            isLocked={isLocked}
            slideProgress={slideProgress}
            onSend={onLockedSend}
            onCancel={onLockedCancel}
          />
          {!isLocked && (
            <div className="flex justify-end">
              <LockIndicator lockProgress={lockProgress} />
            </div>
          )}
        </div>
      ) : (
        <>
          {hasAttachment && (
            <div className="px-4 pt-3">
              <FileAttachmentPreview />
              <MediaAttachmentPreview />
            </div>
          )}

          <div className="flex items-end">
            <div className="flex shrink-0 px-5 pb-6 text-secondary">
              {IS_MOBILE ? <MobileAttachmentMenu /> : <DesktopAttachmentMenu />}
            </div>

            <Textarea
              {...field}
              ref={(e) => {
                field.ref(e);
                textareaRef.current = e;
              }}
              placeholder={t("editor.chatPlaceholder")}
              rows={1}
              className="max-h-72 flex-1 resize-none overflow-y-auto border-0 bg-transparent py-4 pr-3 text-lg shadow-none placeholder:opacity-70 focus-visible:border-transparent focus-visible:ring-0 dark:bg-transparent md:text-lg"
              {...{
                name: field.name,
                onBlur: field.onBlur,
                onKeyDown: handleKeyDown,
                onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  field.onChange(e.target.value);
                  handleTextareaResize(e);
                },
                value: field.value,
                disabled: field.disabled,
              }}
            />

            <div className="flex shrink-0 items-center px-3 pb-3">
              {canSendMessage ? (
                <Button
                  size="icon"
                  variant="ghost"
                  disabled={!canSubmit}
                  type="submit"
                  className="flex size-10 items-center justify-center rounded-full bg-foreground p-2 align-middle hover:bg-secondary hover:text-secondary-foreground lg:cursor-pointer"
                >
                  {!isSubmitting ? (
                    <MoveUp className="size-5 text-background" />
                  ) : (
                    <RefreshCcw className="animate-spin text-background" />
                  )}
                </Button>
              ) : (
                <RecordVoiceMessageButton
                  requestMic={requestMic}
                  startRecording={onStartRecording}
                  voiceRecordingState={voiceRecordingStatus}
                  onGestureStart={onGestureStart}
                  onImmediateSend={onImmediateSend}
                  onImmediateLock={onImmediateLock}
                  className="bg-primary text-primary-foreground"
                />
              )}
            </div>
          </div>

          {showCounter && (
            <div aria-live="polite" className="px-4 pb-2">
              {isOverLimit ? (
                <div className="flex items-center justify-between rounded-md border border-destructive/60 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  <span>{t("editor.messageTooLarge")}</span>
                  <span className="font-mono ml-3 text-[11px]">
                    {messageLimits.charCount.toLocaleString()}
                    {"/"}
                    {messageLimits.charLimit.toLocaleString()}
                  </span>
                </div>
              ) : (
                <div className="flex justify-end text-xs text-muted-foreground">
                  <span>
                    ~{messageLimits.approxRemainingChars.toLocaleString()}{" "}
                    {t("editor.charactersRemaining", {
                      defaultValue: "characters remaining",
                    })}{" "}
                    ({messageLimits.charCount.toLocaleString()}
                    {"/"}
                    {messageLimits.charLimit.toLocaleString()})
                  </span>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
